import { useCallback, useEffect, useState } from 'react'

import { FileText, Xmark } from '@gravity-ui/icons'
import { Alert, Button, Modal, Spinner, toast } from '@heroui/react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { reportsApi } from '@/api/reports.api'
import { DuplicateUploadModal } from '@/components/common/DuplicateUploadModal'
import type { ImportPaidRoadsResponse } from '@/types/report'
import type { DuplicateFileWarning } from '@/types/uploads'

interface UploadPaidRoadsDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onSuccess?: () => void
}

export function UploadPaidRoadsDialog({ isOpen, onOpenChange, onSuccess }: UploadPaidRoadsDialogProps) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [importResult, setImportResult] = useState<ImportPaidRoadsResponse | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateFileWarning | null>(null)

  // Reset state when dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setFile(null)
      setImportResult(null)
      setDuplicateWarning(null)
    }
  }, [isOpen])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
      setDuplicateWarning(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  })

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleUpload = useCallback(async (forceUpload = false) => {
    if (!file) return
    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await reportsApi.importPaidRoads(formData, forceUpload)
      setImportResult(response)
      setDuplicateWarning(null)

      toast.success(t('reports.upload_paid_roads_dialog.success_title'), {
        description: t('reports.upload_paid_roads_dialog.success_desc', {
          paidRoads: response.ImportedPaidRoadRows,
          fuel: response.ImportedFuelRows,
        }),
      })
      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      if (error?.status === 409 && error?.data) {
        setDuplicateWarning(error.data as DuplicateFileWarning)
      } else {
        console.error('Paid roads & fuel upload failed:', error)
        toast.danger(t('reports.upload_paid_roads_dialog.error_title'), {
          description: (error as Error).message || t('reports.upload_paid_roads_dialog.error_desc'),
        })
      }
    } finally {
      setIsUploading(false)
    }
  }, [file, t, onSuccess])

  const handleCancel = useCallback(() => {
    setFile(null)
    setImportResult(null)
    setDuplicateWarning(null)
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop variant="blur" className="bg-zinc-950/40" isDismissable>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading className="font-bold">
                {importResult !== null
                  ? t('reports.upload_paid_roads_dialog.success_title')
                  : t('reports.upload_paid_roads_dialog.title')}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className="mt-2 space-y-4">
              {importResult !== null ? (
                <div className="space-y-4">
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 bg-zinc-50/30 dark:bg-zinc-900/10 flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex-shrink-0">
                      <FileText width={20} height={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate text-zinc-800 dark:text-zinc-200">
                        {file?.name}
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-500">
                        {file ? formatBytes(file.size) : ''}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col items-center justify-center p-5 rounded-2xl border border-success-200/50 dark:border-success-900/20 bg-success-50/10 dark:bg-success-950/5 text-center">
                      <span className="text-3xl font-bold text-success">
                        {importResult.ImportedPaidRoadRows}
                      </span>
                      <span className="text-xs text-success/80 mt-1 uppercase tracking-wider font-semibold">
                        {t('reports.upload_paid_roads_dialog.imported_paid_roads')}
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-5 rounded-2xl border border-success-200/50 dark:border-success-900/20 bg-success-50/10 dark:bg-success-950/5 text-center">
                      <span className="text-3xl font-bold text-success">
                        {importResult.ImportedFuelRows}
                      </span>
                      <span className="text-xs text-success/80 mt-1 uppercase tracking-wider font-semibold">
                        {t('reports.upload_paid_roads_dialog.imported_fuel')}
                      </span>
                    </div>
                  </div>
                </div>
              ) : file ? (
                <div className="space-y-3">
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 bg-zinc-50/30 dark:bg-zinc-900/10 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex-shrink-0">
                        <FileText width={20} height={20} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate text-zinc-800 dark:text-zinc-200">
                          {file.name}
                        </p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-500">
                          {formatBytes(file.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      onPress={() => setFile(null)}
                      aria-label="Remove file"
                      className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                      isDisabled={isUploading}
                    >
                      <Xmark width={16} height={16} />
                    </Button>
                  </div>
                  <Alert status="warning" className="rounded-xl border border-warning/10">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Description className="text-xs">
                        {t('reports.upload_paid_roads_dialog.response_hint')}
                      </Alert.Description>
                    </Alert.Content>
                  </Alert>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 min-h-[200px] ${
                    isDragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600 bg-zinc-50/50 dark:bg-zinc-900/30'
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div
                      className={`p-4 rounded-full ${
                        isDragActive
                          ? 'bg-primary/10 text-primary'
                          : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'
                      }`}
                    >
                      <FileText width={28} height={28} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                        {isDragActive
                          ? t('reports.upload_paid_roads_dialog.drag_active')
                          : t('reports.upload_paid_roads_dialog.description')}
                      </p>
                      <p className="text-xs text-zinc-400 dark:text-zinc-500">
                        {t('reports.upload_paid_roads_dialog.format_hint')}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" className="mt-2" type="button" onPress={open}>
                      {t('reports.upload_paid_roads_dialog.select_file')}
                    </Button>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="flex gap-3 justify-end mt-6">
              {importResult !== null ? (
                <Button variant="secondary" onClick={handleCancel}>
                  {t('reports.upload_paid_roads_dialog.done')}
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={handleCancel} isDisabled={isUploading}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleUpload()}
                    isDisabled={!file || isUploading}
                  >
                    {isUploading ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" color="current" />
                        <span>{t('reports.upload_paid_roads_dialog.uploading')}</span>
                      </div>
                    ) : (
                      t('reports.upload_paid_roads_dialog.upload')
                    )}
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>

    <DuplicateUploadModal
      isOpen={Boolean(duplicateWarning)}
      warningData={duplicateWarning}
      isUploading={isUploading}
      onConfirm={() => handleUpload(true)}
      onCancel={() => setDuplicateWarning(null)}
    />
    </>
  )
}
